import { useState, useMemo, useEffect } from "react";
import type { RangeValue } from "@/shared/ui";
import type { TaskListItem, TaskPriority, Assignee } from "../types";
import type { ProjectBoardView } from "./useCrossProjectBoard";

interface UseBoardFiltersProps {
  projectBoards: ProjectBoardView[];
  allAssignees: Assignee[];
  isSingleProject: boolean;
  projectId: number | null;
  kpiId: number | null;
  activeTab: string;
  onClearKpi?: () => void;
}

export function useBoardFilters({
  projectBoards,
  allAssignees,
  isSingleProject,
  projectId,
  kpiId,
  activeTab,
  onClearKpi,
}: UseBoardFiltersProps) {
  const [search, setSearch] = useState("");
  const [priorityFilter, setPriorityFilter] = useState<TaskPriority | null>(null);
  const [assigneeFilter, setAssigneeFilter] = useState<number | null>(null);
  const [favoritesOnly, setFavoritesOnly] = useState(false);
  const [kpiFlagOnly, setKpiFlagOnly] = useState(false);
  const [dueDateRange, setDueDateRange] = useState<RangeValue | null>(null);
  const [isBacklogActive, setIsBacklogActive] = useState(false);

  useEffect(() => {
    setIsBacklogActive(false);
  }, [activeTab]);

  const activeProjectBoards = useMemo(() => {
    if (!isSingleProject || projectId === null) return projectBoards;
    return projectBoards.filter((b) => b.project.id === projectId);
  }, [isSingleProject, projectId, projectBoards]);

  const activeProject = isSingleProject ? (activeProjectBoards[0]?.project ?? null) : null;

  const activeAssignees = useMemo(() => {
    if (!isSingleProject) return allAssignees;
    return activeProjectBoards[0]?.assignees ?? [];
  }, [isSingleProject, activeProjectBoards, allAssignees]);

  const allTasks = useMemo(() => {
    const tasks: TaskListItem[] = [];
    for (const board of activeProjectBoards) {
      for (const col of board.columns) {
        tasks.push(...col.tasks);
      }
      tasks.push(...board.backlogColumn.tasks);
    }
    return tasks;
  }, [activeProjectBoards]);

  const kpiTasks = useMemo(() => allTasks.filter((t) => t.task_type === "kpi"), [allTasks]);

  const filteredTaskSet = useMemo(() => {
    let result = allTasks.filter((t) => t.task_type !== "kpi");
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      result = result.filter((t) => t.title.toLowerCase().includes(q));
    }
    if (priorityFilter) {
      result = result.filter((t) => t.priority === priorityFilter);
    }
    if (assigneeFilter !== null) {
      result = result.filter((t) => t.assigned_to === assigneeFilter);
    }
    if (favoritesOnly) {
      result = result.filter((t) => t.is_favorite);
    }
    if (kpiFlagOnly) {
      result = result.filter((t) => t.task_type === "first_level_task");
    }
    if (kpiId) {
      result = result.filter((t) => t.parent_task === kpiId || t.id === kpiId);
    }
    if (dueDateRange?.start || dueDateRange?.end) {
      result = result.filter((t) => {
        if (!t.due_date) return false;
        const taskDate = new Date(t.due_date);
        taskDate.setHours(0, 0, 0, 0);
        if (dueDateRange.start) {
          const start = new Date(dueDateRange.start);
          start.setHours(0, 0, 0, 0);
          if (taskDate < start) return false;
        }
        if (dueDateRange.end) {
          const end = new Date(dueDateRange.end);
          end.setHours(23, 59, 59, 999);
          if (taskDate > end) return false;
        }
        return true;
      });
    }
    return result;
  }, [allTasks, search, priorityFilter, assigneeFilter, favoritesOnly, kpiFlagOnly, kpiId, dueDateRange]);

  const filteredProjectBoards = useMemo(() => {
    return activeProjectBoards.map((board) => ({
      ...board,
      columns: board.columns.map((col) => ({
        ...col,
        tasks: col.tasks.filter((task) => filteredTaskSet.includes(task)),
      })),
      backlogColumn: {
        ...board.backlogColumn,
        tasks: board.backlogColumn.tasks.filter((task) => filteredTaskSet.includes(task)),
      },
    }));
  }, [activeProjectBoards, filteredTaskSet]);

  const backlogTasks = useMemo(() => {
    return filteredProjectBoards.flatMap((b) => b.backlogColumn.tasks);
  }, [filteredProjectBoards]);

  const hasActiveFilter = Boolean(
    search.trim() ||
      priorityFilter ||
      assigneeFilter !== null ||
      favoritesOnly ||
      kpiFlagOnly ||
      dueDateRange?.start ||
      dueDateRange?.end ||
      kpiId,
  );

  function handleResetFilters() {
    setSearch("");
    setPriorityFilter(null);
    setAssigneeFilter(null);
    setFavoritesOnly(false);
    setKpiFlagOnly(false);
    setDueDateRange(null);
    onClearKpi?.();
  }

  return {
    search,
    setSearch,
    priorityFilter,
    setPriorityFilter,
    assigneeFilter,
    setAssigneeFilter,
    favoritesOnly,
    setFavoritesOnly,
    kpiFlagOnly,
    setKpiFlagOnly,
    dueDateRange,
    setDueDateRange,
    isBacklogActive,
    setIsBacklogActive,
    activeProjectBoards,
    activeProject,
    activeAssignees,
    allTasks,
    kpiTasks,
    filteredTaskSet,
    filteredProjectBoards,
    backlogTasks,
    hasActiveFilter,
    handleResetFilters,
  };
}
